import { useState } from "preact/compat"
import { Form } from "@/components/form/Form.tsx"
import { useFormState } from "@/components/form/form_state.tsx"
import { Input } from "@/components/form/Input.tsx"
import { Button } from "@/components/ui/button.tsx"
import { useLocation } from "wouter-preact"
import { updateIntegration, upsertIntegration } from "@/api/integration"
import { normalize_portal_url } from "../portal_url"
import { CenterColumnContainer } from "@/components/LoginScreen.tsx"
import { allIntegrations } from "../integrations"

type TabValue = "portal" | "email" | "other"

const tabs: { value: TabValue; label: string }[] = [
  { value: "portal", label: "Portal" },
  { value: "email", label: "Email" },
  { value: "other", label: "Other" },
]

function getMatchingDistributor(name: string, baseUrl: string): { id: string; name: string; path: string } | null {
  const normalizedName = name.toLowerCase().trim()
  const match = allIntegrations.find(i => i.id === normalizedName || i.name.toLowerCase() === normalizedName)
  if (match) {
    // Use the baseUrl to construct the connect path
    const connectPath =
      baseUrl === "/settings/connect"
        ? `/settings/connect/${match.id}`
        : (match.connectPath ?? `/setup/connect/${match.id}`)
    return { id: match.id, name: match.name, path: connectPath }
  }
  return null
}

interface NewConnectionPageProps {
  redirectTo: string
}

export function NewConnectionPage({ redirectTo }: NewConnectionPageProps) {
  const [, setLocation] = useLocation()
  const [loading, setLoading] = useState(false)
  const [activeTab, setActiveTab] = useState<TabValue>("portal")

  const form = useFormState({
    name: "",
    username: "",
    password: "",
    portal_url: "",
    checks_from_email: "",
    description: "",
  })

  const matchingDistributor = getMatchingDistributor(form.name.value, redirectTo)

  async function onSubmitPortal() {
    if (!form.name.value.trim()) {
      form.set_form_errors(["Please enter a name"])
      return
    }
    if (!form.username.value.trim()) {
      form.set_form_errors(["Please enter a username"])
      return
    }

    setLoading(true)
    form.set_form_errors([])

    const res = await upsertIntegration(form.name.value.toLowerCase().trim(), form.username.value, {
      password: form.password.value,
    })

    setLoading(false)
    if (!res.ok) {
      form.set_form_errors(["Failed to create integration. Please try again."])
      return
    }

    let int = res.value.data
    if (form.portal_url.value.trim()) {
      const metaRes = await updateIntegration(int.id, {
        data: { portal_url: normalize_portal_url(form.portal_url.value) },
      })
      if (!metaRes.ok) {
        form.set_form_errors(["Connection created but failed to save portal URL. Please try again."])
        return
      }
    }

    setLocation(redirectTo)
  }

  async function onSubmitEmail() {
    if (!form.name.value.trim()) {
      form.set_form_errors(["Please enter a name"])
      return
    }
    if (!form.checks_from_email.value.trim()) {
      form.set_form_errors(["Please enter the email address"])
      return
    }

    setLoading(true)
    form.set_form_errors([])

    const res = await upsertIntegration(form.name.value.toLowerCase().trim(), form.checks_from_email.value, {})

    setLoading(false)
    if (!res.ok) {
      form.set_form_errors(["Failed to create integration. Please try again."])
      return
    }
    let int = res.value.data
    const metaRes = await updateIntegration(int.id, { data: { email_from: form.checks_from_email.value.trim() } })
    if (!metaRes.ok) {
      form.set_form_errors(["Connection created but failed to save email details. Please try again."])
      return
    }
    setLocation(redirectTo)
  }

  async function onSubmitOther() {
    if (!form.name.value.trim()) {
      form.set_form_errors(["Please enter a name"])
      return
    }
    if (!form.description.value.trim()) {
      form.set_form_errors(["Please enter a description"])
      return
    }

    setLoading(true)
    form.set_form_errors([])

    const res = await upsertIntegration(form.name.value.toLowerCase().trim(), "NEEDS_USERNAME", {})

    setLoading(false)
    if (!res.ok) {
      form.set_form_errors(["Failed to create integration. Please try again."])
      return
    }
    let int = res.value.data
    const metaRes = await updateIntegration(int.id, { data: { description: form.description.value.trim() } })
    if (!metaRes.ok) {
      form.set_form_errors(["Connection created but failed to save description. Please try again."])
      return
    }
    setLocation(redirectTo)
  }

  function handleSubmit() {
    if (activeTab === "portal") {
      onSubmitPortal()
    } else if (activeTab === "email") {
      onSubmitEmail()
    } else {
      onSubmitOther()
    }
  }

  return (
    <CenterColumnContainer title="New Connection" subtitle="Where do you receive checks from this distributor?">
      <div class="space-y-6">
        <Input
          label="Name"
          type="text"
          name="name"
          placeholder="Distributor name"
          required
          bind={form.name}
          noAutofill
        />

        {matchingDistributor ? (
          <div class="space-y-4">
            <p class="text-sm text-gray-500">We have a dedicated setup flow for {matchingDistributor.name}.</p>
            <Button
              class="w-full bg-gray-900 text-white rounded-lg hover:bg-gray-800"
              onClick={() => setLocation(matchingDistributor.path)}>
              Configure {matchingDistributor.name}
            </Button>
          </div>
        ) : (
          <Form class="space-y-6" onSubmit={handleSubmit} alerts={form.form_alerts}>
            {/* Tabs */}
            <div class="flex border-b border-gray-200">
              {tabs.map(tab => (
                <button
                  key={tab.value}
                  type="button"
                  onClick={() => setActiveTab(tab.value)}
                  class={`flex-1 pb-3 text-sm font-medium border-b-2 -mb-px transition-colors ${
                    activeTab === tab.value
                      ? "border-gray-900 text-gray-900"
                      : "border-transparent text-gray-500 hover:text-gray-700"
                  }`}>
                  {tab.label}
                </button>
              ))}
            </div>

            {activeTab === "portal" && (
              <div class="space-y-4">
                <Input label="Username" type="text" name="username" bind={form.username} noAutofill />
                <Input label="Password" type="password" name="password" bind={form.password} noAutofill />
                <Input
                  label="Portal URL"
                  type="text"
                  name="portal_url"
                  placeholder="example.com"
                  bind={form.portal_url}
                  noAutofill
                />
              </div>
            )}

            {activeTab === "email" && (
              <div class="space-y-4">
                <Input
                  label="Checks are emailed from"
                  type="email"
                  name="checks_from_email"
                  placeholder="email@distributorco.org"
                  bind={form.checks_from_email}
                  noAutofill
                />
              </div>
            )}

            {activeTab === "other" && (
              <div class="space-y-4">
                <p class="text-sm text-gray-500">Our ops team will use this information to manually pull checks in.</p>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    value={form.description.value}
                    onInput={e => {
                      const target = e.target as HTMLTextAreaElement
                      form.set_form_data({ description: target.value })
                    }}
                    placeholder="Describe how checks are received from this distributor..."
                    class="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent min-h-[100px]"
                  />
                </div>
              </div>
            )}

            <Button
              type="submit"
              class="w-full bg-gray-900 text-white rounded-lg hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:ring-offset-2 disabled:bg-gray-300 disabled:cursor-not-allowed"
              disabled={loading || !form.name.value.trim()}>
              {loading ? "Creating..." : "Create Connection"}
            </Button>
          </Form>
        )}
      </div>
    </CenterColumnContainer>
  )
}

export function NewConnectionSetupPage() {
  return <NewConnectionPage redirectTo="/setup/connect/new" />
}
